import axios from "../../axios-config";
import { showToast } from "../../helper/ShowToast";
import { markBillingFlowStarted } from "../../helper/billingReturn";
import { shouldOpenStripeSameTab } from "../../helper/browserPlatform";

/** Current admin page URL for Stripe checkout return (original tab stays put). */
export const getBillingReturnUrl = () => {
    const { origin, pathname, search } = window.location;
    return `${origin}${pathname}${search}`;
};

/** Portal return URL — adds marker so return tab can forward to opener. */
export const getBillingPortalReturnUrl = () => {
    const url = new URL(getBillingReturnUrl());
    url.searchParams.set("billing_return", "1");
    return url.toString();
};

const openStripeCheckout = (url) => {
    if (!url) return false;

    if (shouldOpenStripeSameTab()) {
        // Safari on iOS + macOS — same-tab redirect; Chrome keeps new-tab flow.
        window.location.assign(url);
        return true;
    }

    markBillingFlowStarted();
    const opened = window.open(url, "_blank");
    if (!opened) {
        showToast(
            "info",
            "Please allow pop-ups so billing can open in a new tab."
        );
        return false;
    }
    return true;
};

/** @param {object} details - GET /billing/v1/get-user-billing-status data */
export const getBillingStatus = (details) => {
    return {
        type: 'GETBILLINGSTATUS',
        hasActiveSubscription: details?.hasActiveSubscription,
        subscriptionDetails: details?.subscription || {},
        creditDetails: details?.credits,
        teamBilling: details?.teamBilling || null,
        seatSubscriptions: details?.seatSubscriptions || [],
    };
};

export const getBillingDetails = (setIsLoading) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get('/billing/v1/get-user-billing-status', {
                headers: { Authorization: authCode }
            });
            dispatch(getBillingStatus(response.data.data));
        } catch (error) {
            console.log(error)
        }
    };
};


export const getSubscriptionPlanList = (list, teamBilling = null) => {
    return {
        type: 'GETSUBSCRIPTIONLIST',
        list: list,
        teamBilling: teamBilling || null,
    };
};
export const getCreditPlanList = (list) => {
    return {
        type: 'GETCREDITPLANLIST',
        list: list
    };
}
export const getAudioEngineerPlanList = (list) => {
    return {
        type: 'GETAUDIOENGINEERPLAN',
        list: list
    };
}

export const getPurchases = (data) => {
    return {
        type: 'PURCHASEDATA',
        purchaseData: data
    };
}

export const getPurchasesByMonth = (data) => {
    return {
        type: 'PURCHASEBYMONTH',
        purchaseByMonth: data
    };
}

export const getPurchasesByMonthForROI = (data) => {
    return {
        type: 'PURCHASEBYMONTHFORROI',
        purchaseByMonthForROI: data
    };
}

export const getPurchasesByDuration = (data) => {
    return {
        type: 'PURCHASEBYDURATION',
        purchaseByDuration: data
    };
}

/**
 * @param {string} type - planType (subscription | ai_credit_pack | audio_engineer_pack)
 * @param {function} [setPlanLoading]
 * @param {"main"|"addon"} [seatType] - subscription team seats only
 */
export const getPlanList = (type, setPlanLoading, seatType = "main", serviceKey = "") => {
    return async (dispatch) => {
        // Only set loading if not subscription
        const shouldSetLoading = type !== "subscription";
        if (shouldSetLoading && setPlanLoading) setPlanLoading(true);

        try {
            const authCode = "Bearer " + localStorage.getItem("authToken");
            const params = { type };
            if (type === "subscription") {
                params.seatType = seatType;
            }
            if (serviceKey) {
                params.serviceKey = serviceKey;
            }
            const response = await axios.get("/billing/v1/get-plan-list", {
                headers: { Authorization: authCode },
                params,
            });

            const planData = response.data.data;
            const teamBilling = response.data.teamBilling || null;
            switch (type) {
                case "subscription":
                    dispatch(getSubscriptionPlanList(planData, teamBilling));
                    break;
                case "ai_credit_pack":
                    dispatch(getCreditPlanList(planData));
                    break;
                case "audio_engineer_pack":
                    dispatch(getAudioEngineerPlanList(planData));
                    break;
                default:
                    console.warn(`Unknown plan type: ${type}`);
            }
        } catch (error) {
            console.error("getPlanList error:", error);
        } finally {
            if (shouldSetLoading && setPlanLoading) setPlanLoading(false);
        }
    };
};

export const getSubscriptionPlans = () => {
    return async (dispatch) => {
        try {
            const response = await axios.get("/billing/v1/get-subscription-plan-list", {
                params: { type: "subscription" },
            });
            const planData = response.data.data;
            dispatch(getSubscriptionPlanList(planData));
        } catch (error) {
            console.error("getPlanList error:", error);
        }
    };
};

/**
 * @param {object} data - { planId, returnUrl, seatType?: "main"|"addon", artistProfileId?: string }
 */
export const generateStripeSubscriptionCheckout = (data, setIsLoading) => {
    return async (dispatch) => {
        try {
            if (setIsLoading) setIsLoading(true);
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const seatType = data?.seatType || "main";
            const payload = {
                planId: data.planId,
                returnUrl: data.returnUrl,
                seatType,
            };
            if (data.artistProfileId) {
                payload.artistProfileId = data.artistProfileId;
            }
            const response = await axios.post('/billing/v1/create-subscription-checkout', payload, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                return openStripeCheckout(response.data.url);
            }
            return false;
        } catch (error) {
            showToast('error', error?.response?.data?.message || error?.message)
            console.log(error, 'error')
            return false;
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };
};

const WORKSPACE_SYNC_KEY = "workspaceSynced";

/** Backfill org/artist workspace for legacy users; refetches billing after success. */
export const syncWorkspace = () => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem("authToken");
            await axios.post("/user/v1/sync-workspace", {}, {
                headers: { Authorization: authCode },
            });
            await dispatch(getBillingDetails());
            try {
                const { queryClient } = require("../../queryClient");
                const { invalidateUrStudioQueries } = require("../../queries/urstudio/urstudioScope");
                invalidateUrStudioQueries(queryClient);
            } catch (_) {
                /* optional: QueryClient may be unavailable outside app boot */
            }
        } catch (error) {
            console.error("syncWorkspace error:", error);
        }
    };
};

/**
 * Run sync-workspace once per device after login (idempotent on backend).
 * TODO(legacy-migration): Phase 2 — remove calls from auth/LinkAccounts/artist assign after login API sync is removed.
 * Then rely on POST /user/v1/sync-workspace only for stragglers (or delete this helper entirely).
 */
export const maybeSyncWorkspaceOnce = () => {
    return async (dispatch) => {
        if (localStorage.getItem(WORKSPACE_SYNC_KEY) === "true") return;
        const token = localStorage.getItem("authToken");
        if (!token) return;

        try {
            const authCode = `Bearer ${token}`;
            await axios.post("/user/v1/sync-workspace", {}, {
                headers: { Authorization: authCode },
            });
            localStorage.setItem(WORKSPACE_SYNC_KEY, "true");
            await dispatch(getBillingDetails());
            try {
                const { queryClient } = require("../../queryClient");
                const { invalidateUrStudioQueries } = require("../../queries/urstudio/urstudioScope");
                invalidateUrStudioQueries(queryClient);
            } catch (_) {
                /* optional: QueryClient may be unavailable outside app boot */
            }
        } catch (error) {
            console.error("maybeSyncWorkspaceOnce error:", error);
        }
    };
};

/** Refetch billing + credits (e.g. after artist context switch or Stripe return). */
export const refreshWorkspaceBilling = () => {
    return async (dispatch) => {
        await dispatch(getBillingDetails());
        await dispatch(getCreditDetails());
    };
};

/** Billing + access after subscription or artist link (keeps UI in sync with server). */
export const refreshWorkspaceSession = () => {
    return async (dispatch) => {
        const { fetchAccessContext } = await import("./auth");
        await dispatch(refreshWorkspaceBilling());
        await dispatch(fetchAccessContext());
    };
};

export const getCredits = (details) => {
    return {
        type: 'GETCREDITDETAILS',
        creditDetails: details
    };
}

export const consumeCredits = (creditsToUse) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post('/billing/v1/consume-credits', { creditsToUse: creditsToUse || 1 },
                {
                    headers: { Authorization: authCode }
                });
            dispatch(getCredits(response.data.data));
        } catch (error) {
            console.log(error)
        }
    };
};
export const getCreditDetails = () => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get('/billing/v1/get-user-ai-credits',
                {
                    headers: { Authorization: authCode }
                });
            dispatch(getCredits(response.data.data));
        } catch (error) {
            console.log(error)
        }
    };
};

/**
 * @param {{ returnUrl?: string }|undefined} [data]
 */
export const getCustomerPortalSession = (data, toast, setIsLoading) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const returnUrl = data?.returnUrl || getBillingPortalReturnUrl();
            const response = await axios.get('/billing/v1/get-customer-portal-session', {
                headers: { Authorization: authCode },
                params: { returnUrl },
            });
            if (response.status === 200) {
                return openStripeCheckout(response.data.url);
            }
            return false;
        } catch (error) {
            showToast("error", error?.response?.data?.message || error.message)
            console.log(error, 'error')
            return false;
        } finally {
            // setIsLoading(false)
        }
    };
};

export const createOneTimePaymentIntent = (payload) => {
    return async (dispatch) => {
        try {
            // setIsLoading(true)
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post('/billing/v1/get-one-time-payment-intent', payload, {
                headers: { Authorization: authCode },
            });
            if (response.data.status === 200) {
                return response.data?.clientSecret
            }
        } catch (error) {
            // console.log(error, 'error')
        } finally {
            // setIsLoading(false)
        }
    };
};

export const initCustomerSession = (data, setIsLoading, toast) => {
    return async (dispatch) => {
        try {
            setIsLoading(true)
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post('/billing/v1/init-customer-session', data, {
                headers: { Authorization: authCode },
            });
            if (response.data.status === 200) {
                return response.data
            }
        } catch (error) {
            toast.error(error.message)
        } finally {
            setIsLoading(false)
        }
    };
};

export const getUserPurchases = (queryParams, section) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get('/billing/v1/get-user-purchases', {
                headers: { Authorization: authCode },
                params: queryParams
            });
            if (response.data) {
                if (section === 'all'){
                    dispatch(getPurchases(response.data))
                }
                if (section === 'expenses'){
                    dispatch(getPurchasesByMonth(response.data))
                } 
                if (section === 'roi'){
                    dispatch(getPurchasesByMonthForROI(response.data))
                }
                if (section === 'projectedRevenue'){
                    dispatch(getPurchasesByDuration(response.data))
                }
            }
        } catch (error) {
            showToast("error", error?.response?.data?.message || error.message)
        }
    };
};

/** Stripe statuses that mean funds are authorized (hold) or already captured. */
export const isUrStudioPaymentAuthorized = (status) =>
    status === 'requires_capture' || status === 'succeeded';

/**
 * Register a Ur Studio payment hold after Stripe confirmPayment.
 * Required for mix_engineer / audio_master / mix_enhancer packs (manual capture).
 */
export const registerUrStudioPaymentHold = (payload) => {
    return async () => {
        const authCode = "Bearer " + localStorage.getItem('authToken');
        const response = await axios.post(
            '/billing/v1/urstudio/register-payment-hold',
            payload,
            { headers: { Authorization: authCode } }
        );
        if (response.data?.status === 200) {
            return response.data.data;
        }
        throw new Error(response.data?.message || 'Failed to register payment hold');
    };
};

export const cancelUrStudioPaymentHold = (payload) => {
    return async () => {
        const authCode = "Bearer " + localStorage.getItem('authToken');
        const response = await axios.post(
            '/billing/v1/urstudio/cancel-payment-hold',
            payload,
            { headers: { Authorization: authCode } }
        );
        if (response.data?.status === 200) {
            return response.data.data;
        }
        throw new Error(response.data?.message || 'Failed to cancel payment hold');
    };
};

export const getUrStudioPaymentHold = (params) => {
    return async () => {
        const authCode = "Bearer " + localStorage.getItem('authToken');
        const response = await axios.get('/billing/v1/urstudio/payment-hold', {
            headers: { Authorization: authCode },
            params,
        });
        if (response.data?.status === 200) {
            return response.data.data;
        }
        throw new Error(response.data?.message || 'Failed to fetch payment hold');
    };
};
